import React from 'react';
import {
  Button,
  FilterToolbar,
  FilterToolbarChips,
  FilterToolbarDivider,
  FilterToolbarSearch,
  ToolbarFilterMenu,
  type FilterToolbarChip,
  type MultiSelectOption,
} from '@chatsdet/ui';
import type { ExternalRunDetailSearchParams } from '../../../router';
import { isRunAreaState, type RunAreaState } from '../../../features/external-tests/useExternalRun';
import { AREA_STATE_FILTER_LABEL } from '../../../features/external-tests/areaSummary';

/** Shared empty array so the no-state-filter case keeps a stable reference. */
const EMPTY_STATES: RunAreaState[] = [];

export type ExternalAreasFilterBarProps = {
  filters: Pick<ExternalRunDetailSearchParams, 'areaQ' | 'areaStates'>;
  onFilterChange: (updates: Partial<ExternalRunDetailSearchParams>) => void;
  stateOptions: MultiSelectOption[];
};

/**
 * The areas grid's toolbar: free-text search over the area label plus a
 * state filter, mirroring `ExternalTestsFilterBar`'s toolbar for the flat
 * results view. `areaQ`/`areaStates` are deliberately separate URL params
 * from `q`/`verdicts` — those narrow the server-paginated test table, these
 * narrow the areas grid client-side and are never sent to the API.
 */
export function ExternalAreasFilterBar({
  filters,
  onFilterChange,
  stateOptions,
}: ExternalAreasFilterBarProps) {
  const [searchInput, setSearchInput] = React.useState(filters.areaQ ?? '');

  React.useEffect(() => {
    setSearchInput(filters.areaQ ?? '');
  }, [filters.areaQ]);

  React.useEffect(() => {
    const handle = window.setTimeout(() => {
      const trimmed = searchInput.trim();
      if ((filters.areaQ ?? '') === trimmed) return;
      onFilterChange({ areaQ: trimmed || undefined });
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchInput, filters.areaQ, onFilterChange]);

  const selectedStates = filters.areaStates ?? EMPTY_STATES;

  const chips = React.useMemo<FilterToolbarChip[]>(() => {
    const list: FilterToolbarChip[] = [];
    if (filters.areaQ) list.push({ id: 'q', label: `Search: ${filters.areaQ}` });
    selectedStates.forEach((state) => {
      list.push({
        id: `state:${state}`,
        label: `State: ${AREA_STATE_FILTER_LABEL[state]}`,
      });
    });
    return list;
  }, [filters.areaQ, selectedStates]);

  const hasActiveFilters = chips.length > 0;

  const handleClearAll = React.useCallback(() => {
    setSearchInput('');
    onFilterChange({ areaQ: undefined, areaStates: undefined });
  }, [onFilterChange]);

  const handleChipRemove = React.useCallback(
    (chipId: string) => {
      if (chipId === 'q') {
        setSearchInput('');
        onFilterChange({ areaQ: undefined });
      } else if (chipId.startsWith('state:')) {
        const state = chipId.split(':', 2)[1] ?? '';
        const next = selectedStates.filter((s) => s !== state);
        onFilterChange({ areaStates: next.length > 0 ? next : undefined });
      }
    },
    [onFilterChange, selectedStates]
  );

  return (
    // The areas table below is always mounted (even with zero filtered
    // rows), so the bar always fuses to it: square bottom corners, no
    // bottom border.
    <FilterToolbar attached>
      <FilterToolbarSearch
        aria-label="Search areas"
        placeholder="Search areas..."
        value={searchInput}
        onChange={setSearchInput}
      />

      <ToolbarFilterMenu
        label="State"
        options={stateOptions}
        selectedIds={selectedStates}
        onChange={(ids) => {
          const states = ids.filter(isRunAreaState);
          onFilterChange({ areaStates: states.length > 0 ? states : undefined });
        }}
        searchable={false}
      />

      <FilterToolbarDivider />

      {hasActiveFilters && (
        <Button variant="ghost" size="sm" className="text-muted-foreground" onPress={handleClearAll}>
          Clear
        </Button>
      )}

      <FilterToolbarChips chips={chips} onRemove={handleChipRemove} />
    </FilterToolbar>
  );
}
